import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { BASE_URL } from "../../const";
import MultiSelectSKU from "../../components/MultiSelectSKU";
import Navbar from "../../components/Navbar";

// ─── Constants ────────────────────────────────────────────────────────────────

const WAREHOUSE_OPTIONS = [
  { value: "SELECT BRANDS (I) PVT. LTD. (PUNE)", label: "Pune" },
  { value: "SELECT BRANDS (I) PVT. LTD. (KOLKATA)", label: "Kolkata" },
  { value: "DELHI WAREHOUSE", label: "Delhi-1" },
];

const PLANT_TAGGING_OPTIONS = [
  { value: "Best", label: "Best", color: "#2563eb" },
  { value: "Good to Go", label: "Good to Go", color: "#16a34a" },
  { value: "Poor", label: "Poor", color: "#d97706" },
  { value: "Reject", label: "Reject", color: "#dc2626" },
];

const CATEGORY_OPTIONS = [
  { value: "Dried Leaves", label: "Dried Leaves" },
  { value: "uprooted soil", label: "Uprooted Soil" },
  { value: "damaged stem", label: "Damaged Stem" },
  { value: "Less Bushy", label: "Less Bushy" },
];

const IMAGE_TYPE_KEYS = [
  { key: "front_image_field", label: "Front" },
  { key: "group_image_field", label: "Group" },
  { key: "rejected_image_field", label: "Rejected" },
];

const today = new Date().toISOString().split("T")[0];
const EMPTY_FORM = { plant_photo_type: '', plant_tagging: '', category: '', remarks: '' };
const formatLabel = (key) => key.replace("_image_field", "").replace(/_/g, " ");
const getImageKey = (item) => `${item.id}-${item.imageType}`;

// ─── Small reusable pieces ───────────────────────────────────────────────────

const FieldLabel = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 8px" }}>
    {children}
  </p>
);

const NativeSelect = ({ value, onChange, options, placeholder }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ height: 38, width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", padding: "0 10px", fontSize: 13, color: "#374151", outline: "none", cursor: "pointer" }}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const PillGroup = ({ options, value, onChange, multi = false, colorMap = {} }) => {
  const isActive = (v) => (multi ? value.includes(v) : value === v);
  const toggle = (v) => {
    if (!multi) return onChange(v === value ? "" : v);
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map((o) => {
        const active = isActive(o.value ?? o);
        const col = colorMap[o.value] || "#2563eb";
        return (
          <button
            key={o.value ?? o}
            type="button"
            onClick={() => toggle(o.value ?? o)}
            style={{
              padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
              border: `1.5px solid ${active ? col : "#e2e8f0"}`,
              background: active ? col : "#fff",
              color: active ? "#fff" : "#374151",
            }}
          >
            {o.label ?? o}
          </button>
        );
      })}
    </div>
  );
};

const Spinner = ({ size = 20, color = "#2563eb" }) => (
  <>
    <span style={{ width: size, height: size, border: `2px solid #e2e8f0`, borderTopColor: color, borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const GalleryPageSingle = () => {
  const token = localStorage.getItem("token");
  const storeId = localStorage.getItem("store");
  const navigate = useNavigate();

  const [skuOptions, setSkuOptions] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [warehouse, setWarehouse] = useState("");
  const [batchId, setBatchId] = useState("");
  const [remarksFilter, setRemarksFilter] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [activeImageTypes, setActiveImageTypes] = useState(IMAGE_TYPE_KEYS.map((t) => t.key));
  const [quickSearch, setQuickSearch] = useState("");
  const [selectedImageKeys, setSelectedImageKeys] = useState({});

  const [galleryData, setGalleryData] = useState([]);
  const [pagination, setPagination] = useState({ next: null, previous: null, count: 0 });
  const [localRemarks, setLocalRemarks] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [remarksForm, setRemarksForm] = useState(EMPTY_FORM);
  const [isSubmittingRemarks, setIsSubmittingRemarks] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    axios.get(`${BASE_URL}/sku-options/`, { headers: { Authorization: `Token ${token}`, Store: storeId } })
      .then((res) => {
        const unique = new Map();
        (res.data || []).forEach((item) => {
          const value = String(item?.sku || "").trim();
          if (!value || unique.has(value)) return;
          unique.set(value, {
            value,
            label: String(item?.sku_lot_name || value).trim(),
          });
        });
        const normalized = Array.from(unique.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );
        setSkuOptions(normalized);
      })
      .catch(console.error);
  }, [token, storeId]);

  useEffect(() => {
    let images = [];
    galleryData.forEach((item) => {
      IMAGE_TYPE_KEYS.forEach(({ key }) => {
        const url = item[key];
        if (!activeImageTypes.includes(key)) return;
        if (availabilityFilter === "available" && !url) return;
        if (availabilityFilter === "blank" && url) return;
        images.push({ ...item, imageType: key, imageUrl: url || null, hasRemark: localRemarks.some((r) => r.quality_check === item.id && r.plant_photo_type === key) });
      });
    });
    if (remarksFilter === "pending") images = images.filter((img) => !img.hasRemark);
    else if (remarksFilter === "given") images = images.filter((img) => img.hasRemark);
    setAllImages(images);
  }, [galleryData, activeImageTypes, localRemarks, remarksFilter, availabilityFilter]);

  const displayImages = useMemo(() => {
    const term = quickSearch.trim().toLowerCase();
    if (!term) return allImages;
    return allImages.filter((img) =>
      String(img?.sku_lot_name || "").toLowerCase().includes(term) ||
      String(img?.sku || "").toLowerCase().includes(term) ||
      String(img?.batch_id || "").toLowerCase().includes(term) ||
      formatLabel(img?.imageType || "").toLowerCase().includes(term)
    );
  }, [allImages, quickSearch]);

  useEffect(() => {
    setSelectedImageKeys((prev) => {
      const validKeys = new Set(displayImages.map((img) => getImageKey(img)));
      const next = {};
      Object.keys(prev).forEach((k) => {
        if (prev[k] && validKeys.has(k)) next[k] = true;
      });
      return next;
    });
  }, [displayImages]);

  const allVisibleChecked = displayImages.length > 0 && displayImages.every((img) => selectedImageKeys[getImageKey(img)]);
  const selectedVisibleCount = displayImages.reduce((acc, img) => acc + (selectedImageKeys[getImageKey(img)] ? 1 : 0), 0);

  const toggleSelectAllVisible = () => {
    setSelectedImageKeys((prev) => {
      const next = { ...prev };
      if (allVisibleChecked) {
        displayImages.forEach((img) => { delete next[getImageKey(img)]; });
      } else {
        displayImages.forEach((img) => { next[getImageKey(img)] = true; });
      }
      return next;
    });
  };

  const toggleImageSelection = (item) => {
    const imageKey = getImageKey(item);
    setSelectedImageKeys((prev) => ({ ...prev, [imageKey]: !prev[imageKey] }));
  };

  const fetchGallery = useCallback(async (url = null) => {
    setIsLoading(true);
    try {
      let apiUrl = url;
      if (!apiUrl) {
        const params = new URLSearchParams();
        if (selectedSkus.length) params.append("sku", selectedSkus.map((o) => o.value).join(","));
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (warehouse) params.append("warehouse", warehouse);
        if (batchId) params.append("batch_id", batchId);
        if (activeImageTypes.length) params.append("image_types", activeImageTypes.join(","));
        apiUrl = `${BASE_URL}/gallery-report?${params.toString()}`;
      }
      const res = await axios.get(apiUrl, { headers: { Authorization: `Token ${token}`, Store: storeId } });
      setGalleryData(res.data.results || []);
      setPagination({ next: res.data.next, previous: res.data.previous, count: res.data.count });
    } catch (err) {
      console.error(err);
      setGalleryData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSkus, startDate, endDate, warehouse, batchId, activeImageTypes, token, storeId]);

  const fetchRemarks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (warehouse) params.append("warehouse", warehouse);
      if (batchId) params.append("batch_id", batchId);
      const res = await axios.get(`${BASE_URL}/remarks?${params.toString()}`, { headers: { Authorization: `Token ${token}`, Store: storeId } });
      setLocalRemarks(res.data || []);
    } catch (err) { console.error(err); }
  }, [startDate, endDate, warehouse, batchId, token, storeId]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    await Promise.all([fetchGallery(), fetchRemarks()]);
    setIsLoading(false);
  };

  const handlePageChange = (url) => Promise.all([fetchGallery(url), fetchRemarks()]);

  const syncForm = useCallback((image) => {
    const ex = localRemarks.find((r) => r.quality_check === image.id && r.plant_photo_type === image.imageType);
    setRemarksForm(ex
      ? { plant_photo_type: image.imageType, plant_tagging: ex.plant_tagging || '', category: ex.category || '', remarks: ex.remarks || '' }
      : { ...EMPTY_FORM, plant_photo_type: image.imageType });
  }, [localRemarks]);

  const handleImageClick = (item, imageType) => {
    const index = allImages.findIndex((img) => img.id === item.id && img.imageType === imageType);
    const image = { ...item, imageType, imageUrl: item[imageType] };
    setSelectedImage(image);
    setCurrentImageIndex(index);
    syncForm(image);
    setMessage(null);
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setSelectedImage(null); setMessage(null); };

  const navigateImages = useCallback((direction) => {
    if (!allImages.length) return;
    const newIndex = direction === 'next' ? (currentImageIndex + 1) % allImages.length : (currentImageIndex - 1 + allImages.length) % allImages.length;
    const newImage = allImages[newIndex];
    setCurrentImageIndex(newIndex);
    setSelectedImage(newImage);
    syncForm(newImage);
    setMessage(null);
  }, [currentImageIndex, allImages, syncForm]);

  useEffect(() => {
    if (!isModalOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') navigateImages('prev');
      if (e.key === 'ArrowRight') navigateImages('next');
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isModalOpen, navigateImages]);

  const postRemark = (payload) =>
    axios.post(`${BASE_URL}/add-remark`, payload, { headers: { Authorization: `Token ${token}`, Store: storeId } });

  const showMsg = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 2500); };

  const handleRemarksSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingRemarks(true);
    try {
      const res = await postRemark({ quality_check_id: selectedImage.id, plant_photo_type: selectedImage.imageType, ...remarksForm });
      if (res.data.success) {
        await fetchRemarks();
        setAllImages((prev) => prev.map((img) => img.id === selectedImage.id && img.imageType === selectedImage.imageType ? { ...img, hasRemark: true } : img));
        showMsg("success", res.data.created ? "Remark added!" : "Remark updated!");
      }
    } catch (err) { showMsg("error", "Error submitting remark."); }
    finally { setIsSubmittingRemarks(false); }
  };

  const handleOkAndNext = async () => {
    if (!selectedImage) return;
    setIsSubmittingRemarks(true);
    try {
      const res = await postRemark({ quality_check_id: selectedImage.id, plant_photo_type: selectedImage.imageType, plant_tagging: "Good to Go", category: "", remarks: "" });
      if (res.status === 200) {
        setLocalRemarks((prev) => [...prev, { id: res.data?.id || "", quality_check: selectedImage.id, plant_photo_type: selectedImage.imageType, plant_tagging: "Good to Go", category: "", remarks: "" }]);
        setGalleryData((prev) => prev.map((img) => img.id === selectedImage.id ? { ...img, hasRemark: true } : img));
        navigateImages("next");
      }
    } catch (err) { showMsg("error", "Error submitting OK."); }
    finally { setIsSubmittingRemarks(false); }
  };

  const imageRemarks = selectedImage ? localRemarks.find((r) => r.quality_check === selectedImage.id && r.plant_photo_type === selectedImage.imageType) : null;

  // ─── Shared styles ────────────────────────────────────────────────────────
  const s = {
    page: { minHeight: "100vh", background: "#f1f5f9", fontFamily: "inherit" },
    topbar: { background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
    card: { background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0" },
    inputBase: { height: 38, width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", padding: "0 10px", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
    btn: (bg, disabled) => ({ padding: "8px 20px", borderRadius: 8, background: disabled ? "#93c5fd" : bg, color: "#fff", border: "none", fontWeight: 600, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }),
    ghostBtn: (disabled) => ({ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)", color: "#e2e8f0", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", fontWeight: 500 }),
  };

  return (
    <div style={s.page}>
      <Navbar title="Dashboard" />

      {/* Top bar */}
      <div style={s.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 4, height: 22, background: "#2563eb", borderRadius: 2 }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>Gallery Dashboard</span>
        </div>
        <button onClick={() => navigate("/secure/remarks-summary")} style={s.btn("#2563eb", false)}>
          Remarks Summary
        </button>
      </div>

      <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ── Filter Panel ── */}
        <div style={s.card}>
          <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #f1f5f9" }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#64748b" }}>FILTERS</span>
          </div>
          <form onSubmit={handleSearch} style={{ padding: "18px 20px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 14, marginBottom: 18 }}>
              <div>
                <FieldLabel>Plant Name</FieldLabel>
                <MultiSelectSKU options={skuOptions} selected={selectedSkus} onChange={setSelectedSkus} placeholder="Select plants" />
              </div>
              <div>
                <FieldLabel>Start Date</FieldLabel>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={s.inputBase} />
              </div>
              <div>
                <FieldLabel>End Date</FieldLabel>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={s.inputBase} />
              </div>
              <div>
                <FieldLabel>Warehouse</FieldLabel>
                <NativeSelect value={warehouse} onChange={setWarehouse} options={WAREHOUSE_OPTIONS} placeholder="All Warehouses" />
              </div>
              <div>
                <FieldLabel>Batch ID</FieldLabel>
                <input type="text" value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="Enter batch ID" style={s.inputBase} />
              </div>
              <div>
                <FieldLabel>Remarks Status</FieldLabel>
                <NativeSelect value={remarksFilter} onChange={setRemarksFilter} options={[{ value: "all", label: "All" }, { value: "pending", label: "Pending" }, { value: "given", label: "Remarked" }]} />
              </div>
              <div>
                <FieldLabel>Image Availability</FieldLabel>
                <NativeSelect value={availabilityFilter} onChange={setAvailabilityFilter} options={[{ value: "all", label: "All" }, { value: "available", label: "Available Only" }, { value: "blank", label: "Blank / Missing" }]} />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
              <div>
                <FieldLabel>Image Type</FieldLabel>
                <PillGroup
                  options={IMAGE_TYPE_KEYS.map(({ key, label }) => ({ value: key, label }))}
                  value={activeImageTypes}
                  onChange={setActiveImageTypes}
                  multi
                />
              </div>
              <button type="submit" disabled={isLoading} style={s.btn("#2563eb", isLoading)}>
                {isLoading ? <><Spinner size={14} color="#fff" /> Searching...</> : "Search Gallery"}
              </button>
            </div>
          </form>
        </div>

        {/* ── Gallery Grid ── */}
        <div style={s.card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Images</span>
              {displayImages.length > 0 && (
                <span style={{ background: "#eff6ff", color: "#2563eb", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{displayImages.length}</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <input
                type="text"
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                placeholder="Search SKU / Plant / Batch / Type..."
                style={{ ...s.inputBase, minWidth: 260, maxWidth: 340 }}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#475569", cursor: displayImages.length ? "pointer" : "not-allowed" }}>
                <input
                  type="checkbox"
                  checked={allVisibleChecked}
                  onChange={toggleSelectAllVisible}
                  disabled={!displayImages.length}
                />
                {displayImages.length
                  ? `Select visible (${selectedVisibleCount}/${displayImages.length})`
                  : "No visible images"}
              </label>
              {pagination.count > 0 && <span style={{ fontSize: 12, color: "#94a3b8" }}>{pagination.count} total records</span>}
            </div>
          </div>

          <div style={{ padding: 16 }}>
            {isLoading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", gap: 12, color: "#94a3b8" }}>
                <Spinner size={32} />
                <span style={{ fontSize: 13 }}>Loading images...</span>
              </div>
            ) : displayImages.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
                {displayImages.map((item) => (
                  <div
                    key={`${item.id}-${item.imageType}`}
                    onClick={() => handleImageClick(item, item.imageType)}
                    style={{ borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden", cursor: "pointer", background: "#fff", transition: "box-shadow 0.15s, transform 0.15s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(37,99,235,0.14)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                  >
                    <div style={{ aspectRatio: "1", background: "#f8fafc", position: "relative", overflow: "hidden" }}>
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.sku_lot_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#cbd5e1" }}>
                          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909" />
                          </svg>
                          <span style={{ fontSize: 10, marginTop: 4 }}>No image</span>
                        </div>
                      )}
                      <input
                        type="checkbox"
                        checked={!!selectedImageKeys[getImageKey(item)]}
                        onChange={() => toggleImageSelection(item)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: "absolute", top: 8, left: 8, width: 16, height: 16, cursor: "pointer" }}
                        title="Select image"
                      />
                      <span style={{ position: "absolute", top: 6, right: 6, background: "rgba(15,23,42,0.65)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20, textTransform: "capitalize", backdropFilter: "blur(3px)" }}>
                        {formatLabel(item.imageType)}
                      </span>
                    </div>
                    <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                      <p style={{ fontSize: 11, fontWeight: 600, color: "#1e293b", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {item.sku_lot_name}
                      </p>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 12, flexShrink: 0, background: item.hasRemark ? "#dcfce7" : "#fef9c3", color: item.hasRemark ? "#166534" : "#854d0e" }}>
                        {item.hasRemark ? "Done" : "Pending"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", color: "#cbd5e1", gap: 10 }}>
                <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span style={{ fontSize: 13, color: "#94a3b8" }}>No images found. Adjust your filters and search.</span>
              </div>
            )}
          </div>

          {(pagination.previous || pagination.next) && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
              <button disabled={!pagination.previous} onClick={() => handlePageChange(pagination.previous)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: pagination.previous ? "#374151" : "#cbd5e1", fontSize: 13, cursor: pagination.previous ? "pointer" : "not-allowed", fontWeight: 500 }}>
                <FaChevronLeft size={10} /> Previous
              </button>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>{galleryData.length} / {pagination.count}</span>
              <button disabled={!pagination.next} onClick={() => handlePageChange(pagination.next)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: pagination.next ? "#374151" : "#cbd5e1", fontSize: 13, cursor: pagination.next ? "pointer" : "not-allowed", fontWeight: 500 }}>
                Next <FaChevronRight size={10} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal ── */}
      {isModalOpen && selectedImage && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div style={{ background: "#fff", width: "100%", maxWidth: 1120, borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "93vh", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid #e2e8f0", flexShrink: 0 }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", margin: 0 }}>{selectedImage.sku_lot_name}</p>
                <p style={{ fontSize: 12, color: "#64748b", margin: 0, textTransform: "capitalize" }}>
                  {formatLabel(selectedImage.imageType)} image
                  <span style={{ marginLeft: 8, fontSize: 11, background: "#eff6ff", color: "#2563eb", borderRadius: 12, padding: "1px 8px", fontWeight: 700 }}>
                    {currentImageIndex + 1} / {allImages.length}
                  </span>
                </p>
              </div>
              {/* Close button — right-aligned via flex justify-between */}
              <button
                onClick={closeModal}
                style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 20, color: "#64748b", lineHeight: 1, flexShrink: 0 }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>

              {/* Left: image */}
              <div style={{ flex: "0 0 55%", display: "flex", flexDirection: "column", background: "#0f172a" }}>
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflow: "hidden" }}>
                  {selectedImage.imageUrl ? (
                    <img src={selectedImage.imageUrl} alt="Selected" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 6 }} />
                  ) : (
                    <div style={{ textAlign: "center", color: "#475569" }}>
                      <svg width="52" height="52" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1} style={{ margin: "0 auto 8px", display: "block" }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909" />
                      </svg>
                      <p style={{ fontSize: 13, margin: 0 }}>No image available</p>
                    </div>
                  )}
                </div>
                {/* Nav */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#1e293b", flexShrink: 0 }}>
                  <button onClick={() => navigateImages('prev')} style={s.ghostBtn(false)}>
                    <FaChevronLeft size={10} /> Prev
                  </button>
                  <div style={{ display: "flex", gap: 8 }}>
                    {!imageRemarks && (
                      <button onClick={handleOkAndNext} disabled={isSubmittingRemarks}
                        style={{ padding: "7px 16px", borderRadius: 7, background: "#16a34a", color: "#fff", border: "none", fontWeight: 700, fontSize: 13, cursor: isSubmittingRemarks ? "not-allowed" : "pointer" }}>
                        {isSubmittingRemarks ? "..." : "✓ OK & Next"}
                      </button>
                    )}
                    <button onClick={() => navigateImages('next')} style={s.ghostBtn(false)}>
                      Next <FaChevronRight size={10} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: info + form */}
              <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>

                {/* Batch info */}
                <div style={{ padding: "18px 20px", borderBottom: "1px solid #f1f5f9" }}>
                  <FieldLabel>Batch Info</FieldLabel>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
                    {[["Batch ID", selectedImage.batch_id], ["Plant", selectedImage.sku_lot_name], ["SKU", selectedImage.sku], ["Date", selectedImage.date], ["Warehouse", selectedImage.warehouse], ["Quantity", selectedImage.qty], ["Rejected Qty", selectedImage.rejected_qty || "0"], ["Status", selectedImage.status]].map(([label, value]) => (
                      <div key={label}>
                        <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 2px", fontWeight: 500 }}>{label}</p>
                        <p style={{ fontSize: 13, color: "#0f172a", margin: 0, fontWeight: 600 }}>{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Remark form */}
                <div style={{ padding: "18px 20px", flex: 1 }}>
                  <FieldLabel>{imageRemarks ? "Update Remark" : "Add Remark"}</FieldLabel>

                  {imageRemarks && (
                    <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
                      <p style={{ margin: "0 0 3px", fontWeight: 700, color: "#166534" }}>✓ {imageRemarks.plant_tagging}</p>
                      {imageRemarks.category && <p style={{ margin: "0 0 2px", color: "#15803d" }}>Category: {imageRemarks.category}</p>}
                      {imageRemarks.remarks && <p style={{ margin: 0, color: "#15803d" }}>{imageRemarks.remarks}</p>}
                    </div>
                  )}

                  <form onSubmit={handleRemarksSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#374151", margin: "0 0 8px" }}>Plant Quality</p>
                      <PillGroup
                        options={PLANT_TAGGING_OPTIONS}
                        value={remarksForm.plant_tagging}
                        onChange={(val) => setRemarksForm((f) => ({ ...f, plant_tagging: val }))}
                        colorMap={Object.fromEntries(PLANT_TAGGING_OPTIONS.map((o) => [o.value, o.color]))}
                      />
                    </div>

                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#374151", margin: "0 0 8px" }}>Category</p>
                      <PillGroup
                        options={CATEGORY_OPTIONS}
                        value={remarksForm.category}
                        onChange={(val) => setRemarksForm((f) => ({ ...f, category: val }))}
                        colorMap={CATEGORY_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: "#7c3aed" }), {})}
                      />
                    </div>

                    <div>
                      <p style={{ fontSize: 12, fontWeight: 600, color: "#374151", margin: "0 0 8px" }}>Remarks</p>
                      <textarea
                        placeholder="Enter remarks..."
                        value={remarksForm.remarks}
                        onChange={(e) => setRemarksForm((f) => ({ ...f, remarks: e.target.value }))}
                        rows={3}
                        style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: 13, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box", color: "#0f172a" }}
                        onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                        onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                      />
                    </div>

                    <button type="submit" disabled={isSubmittingRemarks} style={s.btn("#2563eb", isSubmittingRemarks)}>
                      {isSubmittingRemarks ? <><Spinner size={14} color="#fff" /> Saving...</> : "Save Remark"}
                    </button>

                    {message && (
                      <p style={{ textAlign: "center", fontSize: 13, margin: 0, fontWeight: 600, color: message.type === "success" ? "#16a34a" : "#dc2626" }}>
                        {message.text}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPageSingle;
